import { useCallback, useEffect, useMemo, useState } from 'react'
import { adminApi } from '@/services/adminApi'
import type {
    DeliveryStats,
    NotificationDelivery,
    NotificationChannel,
    NotificationDeliveryStatus,
} from '@/types/communications'
import {
    RefreshCcw,
    Search,
    ChevronDown,
    ChevronRight,
    Mail,
    Bell,
    Smartphone,
    AlertCircle,
    CheckCircle2,
    Clock,
    XCircle,
    Ban,
    Eye,
    MousePointerClick,
    Inbox,
} from 'lucide-react'
import {
    Badge,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    EmptyState,
    LoadingPanel,
    PillButton,
    SectionTitle,
    StatCard,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from './_shared'

const CHANNELS: NotificationChannel[] = ['IN_APP', 'PUSH', 'EMAIL']
const STATUSES: NotificationDeliveryStatus[] = [
    'QUEUED',
    'SENT',
    'DELIVERED',
    'FAILED',
    'BOUNCED',
    'COMPLAINED',
    'OPENED',
    'CLICKED',
    'SUPPRESSED',
]

export function DeliveryLogTab() {
    const [stats, setStats] = useState<DeliveryStats | null>(null)
    const [items, setItems] = useState<NotificationDelivery[]>([])
    const [nextCursor, setNextCursor] = useState<string | undefined>()
    const [loading, setLoading] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const [expanded, setExpanded] = useState<string | null>(null)
    const [templateKeys, setTemplateKeys] = useState<string[]>([])

    const [channel, setChannel] = useState('')
    const [status, setStatus] = useState('')
    const [templateKey, setTemplateKey] = useState('')
    const [recipient, setRecipient] = useState('')
    const [recipientInput, setRecipientInput] = useState('')

    const filters = useMemo(
        () => ({ channel, status, templateKey, recipient }),
        [channel, status, templateKey, recipient]
    )

    const load = useCallback(
        async (reset: boolean) => {
            if (reset) setLoading(true)
            else setLoadingMore(true)
            try {
                const cursor = reset ? undefined : nextCursor
                const res = await adminApi.getDeliveries({ ...filters, cursor, limit: 50 })
                setItems(prev => (reset ? res.items : [...prev, ...res.items]))
                setNextCursor(res.nextCursor)
            } finally {
                setLoading(false)
                setLoadingMore(false)
            }
        },
        [filters, nextCursor]
    )

    useEffect(() => {
        load(true)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters])

    useEffect(() => {
        adminApi
            .getDeliveryStats()
            .then(setStats)
            .catch(() => undefined)
        adminApi
            .getDeliveryTemplateKeys()
            .then(r => setTemplateKeys(r.templateKeys))
            .catch(() => undefined)
    }, [])

    const refresh = () => {
        load(true)
        adminApi
            .getDeliveryStats()
            .then(setStats)
            .catch(() => undefined)
    }

    const summary = useMemo(() => summarize(stats), [stats])

    return (
        <div className="space-y-6">
            <SectionTitle
                title="Delivery log"
                description="Every email, push, and in-app notification — with provider IDs, opens, and errors."
                actions={
                    <PillButton variant="default" onClick={refresh}>
                        <RefreshCcw className="h-3.5 w-3.5" />
                        Refresh
                    </PillButton>
                }
            />

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard
                    label="Last 24h"
                    value={summary.last24h.total.toLocaleString()}
                    sub={`${summary.last24h.sent} sent · ${summary.last24h.failed} failed · ${summary.last24h.suppressed} suppressed`}
                />
                <StatCard
                    label="Last 7d"
                    value={summary.last7d.total.toLocaleString()}
                    sub={`${summary.last7d.sent} sent · ${summary.last7d.failed} failed · ${summary.last7d.suppressed} suppressed`}
                />
                <StatCard
                    label="All time"
                    value={(stats?.totalAllTime ?? 0).toLocaleString()}
                    sub={stats ? `Updated ${formatDate(stats.generatedAt)}` : '—'}
                />
                <StatCard
                    label="Open rate (7d)"
                    value={
                        summary.last7d.sent > 0
                            ? `${Math.round((summary.last7d.opened / summary.last7d.sent) * 100)}%`
                            : '—'
                    }
                    sub={`${summary.last7d.opened} opens / ${summary.last7d.sent} sends`}
                />
            </div>

            <Card>
                <CardHeader>
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <CardTitle>Recent deliveries</CardTitle>
                        <div className="flex flex-wrap items-center gap-2">
                            <select
                                value={channel}
                                onChange={e => setChannel(e.target.value)}
                                className="h-9 min-w-[140px] rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            >
                                <option value="">All channels</option>
                                {CHANNELS.map(c => (
                                    <option key={c} value={c}>
                                        {c.toLowerCase()}
                                    </option>
                                ))}
                            </select>
                            <select
                                value={status}
                                onChange={e => setStatus(e.target.value)}
                                className="h-9 min-w-[140px] rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            >
                                <option value="">All statuses</option>
                                {STATUSES.map(s => (
                                    <option key={s} value={s}>
                                        {s.toLowerCase()}
                                    </option>
                                ))}
                            </select>
                            <select
                                value={templateKey}
                                onChange={e => setTemplateKey(e.target.value)}
                                className="h-9 min-w-[180px] max-w-[240px] rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            >
                                <option value="">All templates</option>
                                {templateKeys.map(k => (
                                    <option key={k} value={k}>
                                        {k}
                                    </option>
                                ))}
                            </select>
                            <div className="relative">
                                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                                <input
                                    value={recipientInput}
                                    onChange={e => setRecipientInput(e.target.value)}
                                    onKeyDown={e =>
                                        e.key === 'Enter' &&
                                        setRecipient(recipientInput.trim())
                                    }
                                    placeholder="recipient email…"
                                    className="block h-9 w-56 rounded-md border border-gray-300 py-2 pl-8 pr-3 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                            </div>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="!p-0">
                    {loading && items.length === 0 ? (
                        <LoadingPanel label="Loading deliveries…" />
                    ) : items.length === 0 ? (
                        <div className="p-6">
                            <EmptyState
                                icon={Inbox}
                                title="No deliveries match these filters"
                                description="Try removing a filter or sending a test email."
                            />
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-8"> </TableHead>
                                    <TableHead>When</TableHead>
                                    <TableHead>Channel</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Recipient</TableHead>
                                    <TableHead>Template</TableHead>
                                    <TableHead>Linked notif</TableHead>
                                    <TableHead className="text-right">Provider id</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {items.map(row => (
                                    <DeliveryRow
                                        key={row.id}
                                        row={row}
                                        expanded={expanded === row.id}
                                        onToggle={() =>
                                            setExpanded(cur => (cur === row.id ? null : row.id))
                                        }
                                    />
                                ))}
                            </TableBody>
                        </Table>
                    )}
                    {nextCursor && (
                        <div className="border-t border-gray-200 bg-gray-50 p-3 text-center">
                            <button
                                onClick={() => load(false)}
                                disabled={loadingMore}
                                className="text-sm font-medium text-blue-600 hover:text-blue-700 disabled:opacity-50"
                            >
                                {loadingMore ? 'Loading…' : 'Load more'}
                            </button>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    )
}

function summarize(stats: DeliveryStats | null) {
    const empty = { total: 0, sent: 0, failed: 0, suppressed: 0, opened: 0 }
    const reduce = (rows: DeliveryStats['windows']['last24h']) => {
        return rows.reduce(
            (acc, r) => {
                acc.total += r.count
                if (['SENT', 'DELIVERED', 'OPENED', 'CLICKED'].includes(r.status))
                    acc.sent += r.count
                else if (['FAILED', 'BOUNCED', 'COMPLAINED'].includes(r.status))
                    acc.failed += r.count
                else if (r.status === 'SUPPRESSED') acc.suppressed += r.count
                if (['OPENED', 'CLICKED'].includes(r.status)) acc.opened += r.count
                return acc
            },
            { ...empty }
        )
    }
    return {
        last24h: stats ? reduce(stats.windows.last24h) : empty,
        last7d: stats ? reduce(stats.windows.last7d) : empty,
    }
}

function DeliveryRow({
    row,
    expanded,
    onToggle,
}: {
    row: NotificationDelivery
    expanded: boolean
    onToggle: () => void
}) {
    return (
        <>
            <TableRow onClick={onToggle} className="cursor-pointer">
                <TableCell>
                    {expanded ? (
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                    ) : (
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                    )}
                </TableCell>
                <TableCell className="text-gray-600 tabular-nums">
                    {formatDate(row.createdAt)}
                </TableCell>
                <TableCell>
                    <ChannelChip channel={row.channel} />
                </TableCell>
                <TableCell>
                    <StatusChip status={row.status} />
                </TableCell>
                <TableCell className="max-w-[220px] truncate">
                    {row.recipient || <span className="italic text-gray-300">—</span>}
                </TableCell>
                <TableCell className="font-mono text-xs text-gray-600">
                    {row.templateKey || <span className="font-sans italic text-gray-300">—</span>}
                </TableCell>
                <TableCell className="text-xs">
                    {row.notification ? (
                        <div className="flex flex-col">
                            <span className="max-w-[200px] truncate text-gray-900">
                                {row.notification.title}
                            </span>
                            <span className="text-[10px] uppercase tracking-wider text-gray-400">
                                {row.notification.type}
                            </span>
                        </div>
                    ) : (
                        <span className="italic text-gray-300">—</span>
                    )}
                </TableCell>
                <TableCell className="text-right">
                    <code className="font-mono text-xs text-gray-400">
                        {row.providerMessageId
                            ? row.providerMessageId.slice(0, 12) + '…'
                            : '—'}
                    </code>
                </TableCell>
            </TableRow>
            {expanded && (
                <tr className="bg-gray-50">
                    <td colSpan={8} className="px-8 py-5">
                        <DeliveryDetail row={row} />
                    </td>
                </tr>
            )}
        </>
    )
}

function DeliveryDetail({ row }: { row: NotificationDelivery }) {
    return (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <DetailField
                label="Delivery id"
                value={<code className="font-mono text-xs text-gray-900">{row.id}</code>}
            />
            <DetailField
                label="Provider id"
                value={
                    row.providerMessageId ? (
                        <code className="break-all font-mono text-xs text-gray-900">
                            {row.providerMessageId}
                        </code>
                    ) : (
                        '—'
                    )
                }
            />
            <DetailField
                label="User"
                value={
                    row.user ? (
                        <div>
                            <div className="text-sm text-gray-900">
                                {row.user.name || row.user.email}
                            </div>
                            <code className="font-mono text-xs text-gray-500">
                                {row.user.id}
                            </code>
                        </div>
                    ) : (
                        <span className="text-gray-500">External recipient</span>
                    )
                }
            />
            <DetailField
                label="Template"
                value={
                    row.templateKey ? (
                        <div>
                            <code className="font-mono text-xs text-gray-900">
                                {row.templateKey}
                            </code>
                            {row.templateVersion != null && (
                                <span className="ml-2 text-xs uppercase tracking-wider text-gray-500">
                                    v{row.templateVersion}
                                </span>
                            )}
                        </div>
                    ) : (
                        '—'
                    )
                }
            />
            <DetailField
                label="Timestamps"
                value={
                    <div className="space-y-0.5 text-xs tabular-nums text-gray-600">
                        <Timestamp label="created" value={row.createdAt} />
                        <Timestamp label="sent" value={row.sentAt} />
                        <Timestamp label="delivered" value={row.deliveredAt} />
                        <Timestamp label="opened" value={row.openedAt} />
                        <Timestamp label="clicked" value={row.clickedAt} />
                        <Timestamp label="bounced" value={row.bouncedAt} />
                    </div>
                }
            />
            <DetailField
                label="Linked notification"
                value={
                    row.notification ? (
                        <div>
                            <div className="text-sm text-gray-900">
                                {row.notification.title}
                            </div>
                            <div className="mt-0.5 text-xs text-gray-500">
                                {row.notification.type} · {row.notification.feature}
                            </div>
                            <code className="font-mono text-xs text-gray-500">
                                {row.notification.id}
                            </code>
                        </div>
                    ) : (
                        '—'
                    )
                }
            />
            {row.error && (
                <div className="lg:col-span-3">
                    <div className="mb-1 flex items-center gap-1 text-xs font-medium uppercase tracking-wider text-red-600">
                        <AlertCircle className="h-3 w-3" />
                        Error
                    </div>
                    <pre className="whitespace-pre-wrap break-words rounded border border-red-200 bg-red-50 p-3 font-mono text-xs text-red-900">
                        {row.error}
                    </pre>
                </div>
            )}
            {row.metadata && Object.keys(row.metadata).length > 0 && (
                <div className="lg:col-span-3">
                    <div className="mb-1 text-xs font-medium uppercase tracking-wider text-gray-500">
                        Metadata
                    </div>
                    <pre className="overflow-x-auto whitespace-pre-wrap break-words rounded border border-gray-200 bg-white p-3 font-mono text-xs text-gray-700">
                        {JSON.stringify(row.metadata, null, 2)}
                    </pre>
                </div>
            )}
        </div>
    )
}

function DetailField({
    label,
    value,
}: {
    label: string
    value: React.ReactNode
}) {
    return (
        <div>
            <div className="mb-1 text-xs font-medium uppercase tracking-wider text-gray-500">
                {label}
            </div>
            <div>{value}</div>
        </div>
    )
}

function Timestamp({ label, value }: { label: string; value: string | null }) {
    return (
        <div className="flex justify-between gap-3">
            <span className="w-20 text-xs uppercase tracking-wider text-gray-500">
                {label}
            </span>
            <span className={value ? 'text-gray-900' : 'italic text-gray-300'}>
                {value ? formatDate(value) : '—'}
            </span>
        </div>
    )
}

function ChannelChip({ channel }: { channel: NotificationChannel }) {
    const cfg = {
        EMAIL: { Icon: Mail, label: 'Email', variant: 'default' as const },
        PUSH: { Icon: Smartphone, label: 'Push', variant: 'info' as const },
        IN_APP: { Icon: Bell, label: 'In-app', variant: 'warning' as const },
    }[channel]
    return (
        <Badge variant={cfg.variant}>
            <cfg.Icon className="mr-1 h-3 w-3" />
            {cfg.label}
        </Badge>
    )
}

function StatusChip({ status }: { status: NotificationDeliveryStatus }) {
    const cfg: Record<
        NotificationDeliveryStatus,
        {
            Icon: typeof Mail
            variant: 'default' | 'success' | 'warning' | 'error' | 'info'
        }
    > = {
        QUEUED: { Icon: Clock, variant: 'default' },
        SENT: { Icon: CheckCircle2, variant: 'success' },
        DELIVERED: { Icon: CheckCircle2, variant: 'success' },
        FAILED: { Icon: XCircle, variant: 'error' },
        BOUNCED: { Icon: XCircle, variant: 'warning' },
        COMPLAINED: { Icon: AlertCircle, variant: 'warning' },
        OPENED: { Icon: Eye, variant: 'info' },
        CLICKED: { Icon: MousePointerClick, variant: 'info' },
        SUPPRESSED: { Icon: Ban, variant: 'default' },
    }
    const c = cfg[status]
    return (
        <Badge variant={c.variant}>
            <c.Icon className="mr-1 h-3 w-3" />
            {status.toLowerCase()}
        </Badge>
    )
}

function formatDate(iso: string): string {
    const d = new Date(iso)
    const now = Date.now()
    const diffMs = now - d.getTime()
    const min = Math.round(diffMs / 60_000)
    if (min < 1) return 'just now'
    if (min < 60) return `${min}m ago`
    const hr = Math.round(min / 60)
    if (hr < 24) return `${hr}h ago`
    return d.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}
